import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import uk from './uk';
import en from './en';

const DICTIONARIES = { uk, en };
export const LANGUAGES = [
  { code: 'uk', label: 'Українська', short: 'UA' },
  { code: 'en', label: 'English', short: 'EN' },
];
const STORAGE_KEY = 'adminLang';

const I18nContext = createContext(null);

function lookup(dict, key) {
  return key.split('.').reduce((node, part) => (node && typeof node === 'object' ? node[part] : undefined), dict);
}

function readInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && DICTIONARIES[saved]) return saved;
  } catch { /* приватный режим */ }
  return 'uk';
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLanguage);
  // Валюта сумм по умолчанию: валюта карт открытой компании (задаёт SessionProvider)
  const [currency, setCurrency] = useState('UAH');

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!DICTIONARIES[next]) return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* приватный режим */ }
  }, []);

  // t('a.b', { name }) — перевод с подстановками; нет перевода — украинский, затем сам ключ
  const t = useCallback((key, vars) => {
    let text = lookup(DICTIONARIES[lang], key) ?? lookup(DICTIONARIES.uk, key) ?? key;
    if (typeof text !== 'string') return key;
    if (vars) {
      text = text.replace(/\{(\w+)\}/g, (_, name) => (vars[name] !== undefined ? String(vars[name]) : `{${name}}`));
    }
    return text;
  }, [lang]);

  // Форматирование чисел/денег/дат под язык интерфейса
  const locale = lang === 'en' ? 'en-GB' : 'uk-UA';
  const fmt = useMemo(() => ({
    number: (v, opts) => new Intl.NumberFormat(locale, opts).format(Number(v) || 0),
    money: (v, cur = currency) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: cur || 'UAH', maximumFractionDigits: 0 }).format(Number(v) || 0),
    date: (v) => (v ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(toIso(v))) : '—'),
    dateTime: (v) => (v ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(toIso(v))) : '—'),
    relative: (v) => (v ? relativeTime(toIso(v), locale) : '—'),
    // Время операций кассы (tran_date) — местное время компании без зоны: показываем как есть
    posDateTime: (v) => (v ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(wallClock(v)) : '—'),
    posRelative: (v) => (v ? relativeTime(wallClock(v).toISOString(), locale) : '—'),
  }), [locale, currency]);

  const value = useMemo(() => ({ lang, setLang, t, fmt, setCurrency }), [lang, setLang, t, fmt]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// SQLite отдаёт "2026-10-26 09:00:00" без зоны — это UTC
function toIso(v) {
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s.replace(' ', 'T') + 'Z';
  return s;
}

// «YYYY-MM-DD HH:MM:SS» как настенное время (без пересчёта из UTC)
function wallClock(v) {
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)) : new Date(v);
}

function relativeTime(iso, locale) {
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day');
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso));
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider');
  return ctx;
}

export const useT = () => useI18n().t;
